/**
 * Issues API Client
 * Handles all issue management and assignment operations
 */

import type {
  ValidationResult,
  IssueStatus,
  IssuePriority,
  User,
  IssueNote,
} from "@/types/rules";

const API_BASE_URL =
  import.meta.env.VITE_BACKEND_URL || "http://localhost:8080";

interface ApiResponse<T> {
  success: boolean;
  error?: string;
  [key: string]: any;
}

export class IssuesClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Get all issues
   */
  async getAllIssues(): Promise<ValidationResult[]> {
    const response = await fetch(`${this.baseUrl}/api/issues`);
    const data: ApiResponse<ValidationResult[]> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to fetch issues");
    }

    return data.issues || [];
  }

  /**
   * Get a specific issue
   */
  async getIssue(issueId: string): Promise<ValidationResult> {
    const response = await fetch(`${this.baseUrl}/api/issues/${issueId}`);
    const data: ApiResponse<ValidationResult> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to fetch issue");
    }

    return data.issue;
  }

  /**
   * Create a new issue
   */
  async createIssue(
    validationResult: ValidationResult
  ): Promise<ValidationResult> {
    const response = await fetch(`${this.baseUrl}/api/issues`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(validationResult),
    });

    const data: ApiResponse<ValidationResult> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to create issue");
    }

    return data.issue;
  }

  /**
   * Create multiple issues at once
   */
  async createIssuesBulk(
    validationResults: ValidationResult[]
  ): Promise<ValidationResult[]> {
    const response = await fetch(`${this.baseUrl}/api/issues/bulk`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ issues: validationResults }),
    });

    const data: ApiResponse<ValidationResult[]> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to create issues");
    }

    return data.issues || [];
  }

  /**
   * Update an issue
   */
  async updateIssue(
    issueId: string,
    updates: Partial<ValidationResult>
  ): Promise<ValidationResult> {
    const response = await fetch(`${this.baseUrl}/api/issues/${issueId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updates),
    });

    const data: ApiResponse<ValidationResult> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to update issue");
    }

    return data.issue;
  }

  /**
   * Delete an issue
   */
  async deleteIssue(issueId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/issues/${issueId}`, {
      method: "DELETE",
    });

    const data: ApiResponse<void> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to delete issue");
    }
  }

  /**
   * Assign an issue to a user
   */
  async assignIssue(
    issueId: string,
    assignedTo: User | null,
    assignedBy: User
  ): Promise<ValidationResult> {
    const response = await fetch(
      `${this.baseUrl}/api/issues/${issueId}/assign`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ assignedTo, assignedBy }),
      }
    );

    const data: ApiResponse<ValidationResult> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to assign issue");
    }

    return data.issue;
  }

  /**
   * Add a note to an issue
   */
  async addNote(
    issueId: string,
    note: Omit<IssueNote, "id" | "timestamp">
  ): Promise<ValidationResult> {
    const response = await fetch(
      `${this.baseUrl}/api/issues/${issueId}/notes`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(note),
      }
    );

    const data: ApiResponse<ValidationResult> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to add note");
    }

    return data.issue;
  }

  /**
   * Set issue priority
   */
  async setPriority(
    issueId: string,
    priority: IssuePriority
  ): Promise<ValidationResult> {
    const response = await fetch(
      `${this.baseUrl}/api/issues/${issueId}/priority`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ priority }),
      }
    );

    const data: ApiResponse<ValidationResult> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to set priority");
    }

    return data.issue;
  }

  /**
   * Set issue due date
   */
  async setDueDate(
    issueId: string,
    dueDate: string
  ): Promise<ValidationResult> {
    const response = await fetch(
      `${this.baseUrl}/api/issues/${issueId}/due-date`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ dueDate }),
      }
    );

    const data: ApiResponse<ValidationResult> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to set due date");
    }

    return data.issue;
  }

  /**
   * Mark an issue as resolved
   */
  async resolveIssue(
    issueId: string,
    resolvedBy: User,
    resolutionNote?: string
  ): Promise<ValidationResult> {
    const response = await fetch(
      `${this.baseUrl}/api/issues/${issueId}/resolve`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ resolvedBy, resolutionNote }),
      }
    );

    const data: ApiResponse<ValidationResult> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to resolve issue");
    }

    return data.issue;
  }

  /**
   * Get issue statistics
   */
  async getIssueStats(): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/issues/stats`);
    const data: ApiResponse<any> = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Failed to fetch statistics");
    }

    return data.stats;
  }
}

// Export singleton instance
export const issuesClient = new IssuesClient();
